/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react'
import type { ColDef, ValueSetterParams } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  canEditPlayerEmail,
  canManageFamily,
  type RosterPlayerRow,
} from '@sportninja/common/features/competition/operations/utils/rosterRows'
import type { EditablePlayerField } from '@sportninja/common/features/competition/operations/utils/rosterInlineEdit'
import StatusChip from 'src/components/ts-components/StatusChip'
import PlayerStatusCell from 'src/features/team/player/components/PlayerStatusCell'
import RosterMemberCell from '../components/roster/RosterMemberCell'
import RosterRowActions, {
  type RosterRowAction,
} from '../components/roster/RosterRowActions'
import { OPERATIONS_PILL_CELL_CLASS } from '../components/operationsGridStyle'

const translate = t as (key: string, options?: unknown) => string

interface Options {
  canEdit: boolean
  /** Team-level admin, from `meta.permissions[teamId]` — gates Manage Family. */
  canAdmin: boolean
  /**
   * The roster's `should_show_waiver` — the Team screen shows Signed Waiver
   * only then, so this grid does too.
   */
  showWaiver: boolean
  /**
   * Email column. On by default; the Team screen shows it to roster editors
   * only, as it always has.
   */
  showEmail?: boolean
  /**
   * Invite column. On by default; the Team screen hides it inside the embed
   * iframe.
   */
  showInvite?: boolean
  positionOptions: Array<{ label: string; value: string }>
  /**
   * Inline edit handler. Omitted → read-only cells: the Team screen edits
   * through its Edit Profile modal instead.
   */
  onEditField?: (
    row: RosterPlayerRow,
    field: EditablePlayerField,
    value: string,
    currentValue: string
  ) => void
  onRowAction?: (action: RosterRowAction, row: RosterPlayerRow) => void
  /**
   * Replaces the default ⋯ menu. The Team screen keeps its own menu, which
   * also offers Send Invitation to a player never invited.
   */
  renderActions?: (row: RosterPlayerRow) => ReactNode
  /** Links each name to a profile — the Team screen opens the player page. */
  profileLinkOf?: (row: RosterPlayerRow) => string
  /** Shows the customer identifier under the name, as the Team screen does. */
  showCustomerIdentifier?: boolean
}

/** Position ids mean nothing to a reader — show the sport's label. */
const positionLabel = (
  options: Array<{ label: string; value: string }>,
  positionId: string
): string => options.find((option) => option.value === positionId)?.label ?? '-'

/**
 * Accepted is settled (green); invited is awaiting action (amber, matching the
 * mockup's pending pill); never-invited is the one that needs attention (red).
 */
/**
 * Accepted is settled (green); invited is awaiting action (amber, matching the
 * mockup's pending pill). Never-invited renders NO pill at all — it is the
 * resting state of a newly added player, not a problem, and badging every such
 * row would cry wolf.
 */
const inviteTone = (status: RosterPlayerRow['inviteStatus']): 'ok' | 'warn' =>
  status === 'accepted' ? 'ok' : 'warn'

/** '' for never-invited, so the cell stays empty rather than showing a pill. */
const inviteLabel = (status: RosterPlayerRow['inviteStatus']): string => {
  if (status === 'accepted') {
    return translate('common:accepted')
  }
  if (status === 'invited') {
    return translate('common:invited')
  }
  return ''
}

/**
 * Columns for the Operations roster grid (Players and Spares share one set —
 * a spare is just `is_affiliate`, not a different shape).
 *
 * Editable cells commit on blur, matching the tab's other grids. Each
 * `valueSetter` returns `false` so AG Grid never writes the row optimistically:
 * the save + `refresh()` is the single source of truth, so a rejected edit
 * cannot linger on screen.
 */
export const useRosterPlayersColumns = ({
  canEdit,
  canAdmin,
  showWaiver,
  showEmail = true,
  showInvite = true,
  positionOptions,
  onEditField,
  onRowAction,
  renderActions,
  profileLinkOf,
  showCustomerIdentifier = false,
}: Options): ColDef<RosterPlayerRow>[] => {
  'use memo'

  // Cells edit in place only when the caller can save the edit.
  const canEditInline = canEdit && Boolean(onEditField)

  const editCell =
    (field: EditablePlayerField, currentOf: (row: RosterPlayerRow) => string) =>
    (params: ValueSetterParams<RosterPlayerRow>) => {
      onEditField?.(
        params.data,
        field,
        String(params.newValue ?? ''),
        currentOf(params.data)
      )
      return false
    }

  return [
    {
      colId: 'name',
      field: 'name',
      headerName: capitalizeWords(translate('common:name')),
      // Bounded: a player name does not get better past this width, so the
      // slack goes to the other columns instead.
      flex: 2,
      minWidth: 160,
      maxWidth: 320,
      editable: canEditInline,
      valueSetter: editCell('name', (row) => row.name),
      cellRenderer: ({ data }: CustomCellRendererProps<RosterPlayerRow>) =>
        data ? (
          <RosterMemberCell
            name={data.name}
            initials={data.initials}
            imageUrl={data.imageUrl}
            to={profileLinkOf?.(data)}
            subtitle={
              showCustomerIdentifier ? data.customerIdentifier : undefined
            }
          />
        ) : null,
    },
    {
      colId: 'jersey',
      field: 'jersey',
      headerName: '#',
      width: 70,
      editable: canEditInline,
      valueSetter: editCell('jersey', (row) => row.jersey),
    },
    {
      colId: 'position',
      headerName: capitalizeWords(translate('common:position')),
      flex: 1,
      minWidth: 130,
      editable: canEditInline,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: positionOptions.map((option) => option.value),
      },
      // The editor stores ids; both display paths map back to the label so the
      // cell never shows a bare number.
      refData: Object.fromEntries(
        positionOptions.map((option) => [option.value, option.label])
      ),
      valueGetter: ({ data }: { data?: RosterPlayerRow }) =>
        data?.positionId ?? '',
      valueFormatter: ({ value }: { value?: string }) =>
        positionLabel(positionOptions, String(value ?? '')),
      valueSetter: editCell('position', (row) => row.positionId),
    },
    ...(showEmail
      ? [
          {
            colId: 'email',
            field: 'email',
            headerName: capitalizeWords(translate('common:email')),
            // The longest, most variable value on the row, so it gets the
            // largest share — but bounded like Name, so it cannot swallow the
            // row.
            flex: 2,
            minWidth: 170,
            maxWidth: 320,
            // Locked once the player accepts — the address is then their
            // identity, not a roster field an admin may overwrite.
            editable: ({ data }: { data?: RosterPlayerRow }) =>
              canEditInline &&
              Boolean(data) &&
              canEditPlayerEmail(data!.inviteStatus),
            valueSetter: editCell('email', (row) => row.email),
            cellRenderer: ({
              value,
            }: CustomCellRendererProps<RosterPlayerRow>) =>
              value ? String(value) : '-',
          } as ColDef<RosterPlayerRow>,
        ]
      : []),
    {
      // Same value and format as the Team screen's DOB column.
      colId: 'dob',
      field: 'birthDate',
      headerName: translate('common:dob'),
      flex: 1,
      minWidth: 120,
    },
    ...(showInvite
      ? [
          {
            // Named "Invite" to match the Team screen; Status is the player's
            // Suspended / Injured state, the last column before the actions.
            colId: 'invite',
            headerName: capitalizeWords(translate('Web:invite')),
            flex: 1,
            minWidth: 132,
            cellClass: OPERATIONS_PILL_CELL_CLASS,
            valueGetter: ({ data }: { data?: RosterPlayerRow }) =>
              data ? inviteLabel(data.inviteStatus) : '',
            cellRenderer: ({
              value,
              data,
            }: CustomCellRendererProps<RosterPlayerRow, string>) =>
              data && value ? (
                <StatusChip tone={inviteTone(data.inviteStatus)}>
                  {capitalizeWords(String(value))}
                </StatusChip>
              ) : null,
          } as ColDef<RosterPlayerRow>,
        ]
      : []),
    ...(showWaiver
      ? [
          {
            // Same Yes / No as the Team screen's Signed Waiver column.
            colId: 'waiver',
            headerName: capitalizeWords(translate('common:signedWaiver')),
            // Never narrower than the "Signed Waiver" header plus its sort
            // icon — the content is only Yes / No, so the header sets it.
            flex: 1,
            minWidth: 160,
            valueGetter: ({ data }: { data?: RosterPlayerRow }) =>
              data
                ? translate(data.signedWaiver ? 'common:yes' : 'common:no')
                : '',
          } as ColDef<RosterPlayerRow>,
        ]
      : []),
    {
      // Suspended / Injured / Suspension Ended — the Team screen's Status, from
      // the same shared `toPlayerStatus`. Sorting is off: it is a mixed state,
      // not an ordered value.
      colId: 'status',
      headerName: capitalizeWords(translate('common:status')),
      // Fits the longest date line, "Aug 20, 2026 – Indefinitely", unclipped.
      flex: 1,
      minWidth: 210,
      sortable: false,
      // No autoHeight: pill + date line (~40px) fits the fixed 48px row, and a
      // taller row would leave the plain-text cells beside it sitting high.
      cellClass: OPERATIONS_PILL_CELL_CLASS,
      cellRenderer: ({ data }: CustomCellRendererProps<RosterPlayerRow>) =>
        data ? <PlayerStatusCell status={data.status} /> : null,
    },
    ...(canEdit
      ? [
          {
            colId: 'actions',
            headerName: '',
            // Frozen to the right edge so the menu stays reachable on a wide table.
            pinned: 'right',
            width: 64,
            sortable: false,
            resizable: false,
            cellRenderer: ({
              data,
            }: CustomCellRendererProps<RosterPlayerRow>) =>
              data ? (
                renderActions ? (
                  renderActions(data)
                ) : (
                  <RosterRowActions
                    row={data}
                    onSelect={(action) => onRowAction?.(action, data)}
                    canManageFamily={canManageFamily(
                      data.inviteStatus,
                      canAdmin
                    )}
                  />
                )
              ) : null,
          } as ColDef<RosterPlayerRow>,
        ]
      : []),
  ]
}
